import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info, X } from "lucide-react";

/* The "what does this mean" control beside a KPI or panel title.

   It was a `title` attribute on a <span>, which failed three ways at once: a
   native tooltip needs a full second of hovering before it appears, cannot be
   reached by keyboard, and does nothing whatsoever on a touch screen — so a
   control that looked like a button behaved like decoration.

   Behaviour now:
     hover in   → opens after OPEN_DELAY, so sweeping the pointer across a row
                  of KPIs does not flash six bubbles on the way past
     hover out  → closes after CLOSE_DELAY, which is also the grace period for
                  crossing the gap between the button and the bubble
     click/tap  → opens at once and PINS it, so it survives the pointer leaving
     Escape, a click elsewhere, or the bubble's own ✕ → closes and unpins

   usePosition, useHintDismiss and HintBubble are exported because the revenue
   chart hangs the same bubble off its bars — one implementation and one visual
   design rather than a lookalike. */

const GAP = 8; // space between the button and the bubble
const EDGE = 12; // keep the bubble this far off the viewport edges
const WIDTH = 268;

const OPEN_DELAY = 90;
/* Long enough to cross GAP without the bubble vanishing underneath the
   pointer, short enough that moving away feels like it closed immediately. */
const CLOSE_DELAY = 160;

/* Portalled to <body>. The bubble is wider than the KPI card it belongs to and
   several of its ancestors clip their overflow, so an absolutely positioned one
   was being cut off at the card edge. Position is measured from the trigger and
   written as fixed coordinates instead. */
export const usePosition = (open, anchorRef, anchorKey) => {
	const [pos, setPos] = useState(null);

	const place = useCallback(() => {
		const el = anchorRef.current;
		if (!el) return;

		const r = el.getBoundingClientRect();
		const vw = window.innerWidth;
		const vh = window.innerHeight;

		const width = Math.min(WIDTH, vw - EDGE * 2);
		// Centred on the trigger, then pulled back inside whichever edge it crosses.
		const left = Math.min(Math.max(r.left + r.width / 2 - width / 2, EDGE), vw - width - EDGE);

		/* Flip above when the space below cannot hold it. The bubble's height is
		   not known before it renders, so this reserves a realistic worst case
		   rather than measuring — being wrong here only costs a few pixels of
		   overlap, while a second render pass costs a visible jump. */
		const below = vh - r.bottom;
		const flip = below < 150 && r.top > below;

		setPos({
			left,
			width,
			...(flip ? { bottom: vh - r.top + GAP } : { top: r.bottom + GAP }),
			flip,
		});
	}, [anchorRef]);

	useLayoutEffect(() => {
		if (!open) return undefined;
		place();

		/* Scroll is captured so the bubble also follows an ancestor scrolling,
		   not just the window — a KPI row inside a scrolled section otherwise
		   leaves its hint floating where the card used to be. */
		window.addEventListener("scroll", place, true);
		window.addEventListener("resize", place);
		return () => {
			window.removeEventListener("scroll", place, true);
			window.removeEventListener("resize", place);
		};
		/* anchorKey re-places when the anchor ELEMENT changes while the bubble
		   stays open — the chart slides its bubble from bar to bar without ever
		   closing it, and `place` is memoised on the ref object, which does not
		   change when its .current does. The info buttons pass nothing, their
		   anchor being fixed for the life of the component. */
	}, [open, place, anchorKey]);

	return pos;
};

/* Escape, or a pointer landing outside both the trigger and the bubble, closes
   an open hint. Shared so the chart dismisses exactly like the info buttons
   rather than approximating it. */
export const useHintDismiss = (open, close, anchorRef, bubbleRef, { focusAnchorOnEscape = false } = {}) => {
	useEffect(() => {
		if (!open) return undefined;

		const onKeyDown = (e) => {
			if (e.key === "Escape") {
				e.stopPropagation();
				close();
				// Right for a button the user tabbed to, wrong for a chart bar.
				if (focusAnchorOnEscape) anchorRef.current?.focus?.();
			}
		};

		/* pointerdown rather than click: a tap that opens one hint must close any
		   other before the new one paints, and click fires too late for that. */
		const onPointerDown = (e) => {
			if (anchorRef.current?.contains?.(e.target)) return;
			if (bubbleRef.current?.contains(e.target)) return;
			close();
		};

		window.addEventListener("keydown", onKeyDown, true);
		window.addEventListener("pointerdown", onPointerDown, true);
		return () => {
			window.removeEventListener("keydown", onKeyDown, true);
			window.removeEventListener("pointerdown", onPointerDown, true);
		};
	}, [open, close, anchorRef, bubbleRef, focusAnchorOnEscape]);
};

/* The bubble itself, purely presentational: the caller owns when it opens and
   what anchors it, so one design serves both the info buttons and the chart. */
export const HintBubble = ({ pos, pinned, onClose, onMouseEnter, onMouseLeave, label, bubbleRef, children }) => {
	if (!pos) return null;

	return createPortal(
		<div
			ref={bubbleRef}
			role="tooltip"
			className={`sf-rap-hint ${pos.flip ? "is-above" : ""} ${pinned ? "is-pinned" : ""}`}
			style={{
				left: pos.left,
				width: pos.width,
				...(pos.flip ? { bottom: pos.bottom } : { top: pos.top }),
			}}
			/* Reaching the bubble cancels the close the trigger started, so a hint
			   can be read and its text selected. Leaving it starts the close
			   again — unless it was pinned. */
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
		>
			{children}
			{/* Touch has no hover to end, and tapping the 22px trigger a second
			    time is a poor target. Only offered once pinned, which is the only
			    state a pointer cannot dismiss. */}
			{pinned && onClose && (
				<button type="button" className="sf-rap-hint-close" aria-label={label} onClick={onClose}>
					<X size={13} />
				</button>
			)}
		</div>,
		document.body,
	);
};

const InfoHint = ({ text, label = "What this means" }) => {
	/* Two reasons the bubble can be open, tracked apart.

	   Collapsing them into one boolean looked right and was not: hovering the
	   button opened the bubble, and the click that followed toggled that same
	   boolean straight back to false — so on a mouse the control opened on
	   approach and closed the instant you pressed it. Hover is a preview that
	   goes away on its own; a click pins it until dismissed. */
	const [hovered, setHovered] = useState(false);
	const [pinned, setPinned] = useState(false);
	const open = hovered || pinned;

	const anchorRef = useRef(null);
	const bubbleRef = useRef(null);
	const pos = usePosition(open, anchorRef);

	/* One timer for both directions: entering cancels a pending close and vice
	   versa, which is what stops the bubble flickering when the pointer crosses
	   the gap between the button and the bubble. */
	const timer = useRef(null);
	const clearTimer = useCallback(() => {
		if (timer.current) {
			clearTimeout(timer.current);
			timer.current = null;
		}
	}, []);

	const hoverIn = useCallback(() => {
		clearTimer();
		timer.current = setTimeout(() => setHovered(true), OPEN_DELAY);
	}, [clearTimer]);

	const hoverOut = useCallback(() => {
		clearTimer();
		timer.current = setTimeout(() => setHovered(false), CLOSE_DELAY);
	}, [clearTimer]);

	const close = useCallback(() => {
		clearTimer();
		setPinned(false);
		setHovered(false);
	}, [clearTimer]);

	// A hint left open when its panel unmounts would otherwise leak its timer.
	useEffect(() => clearTimer, [clearTimer]);

	useHintDismiss(open, close, anchorRef, bubbleRef, { focusAnchorOnEscape: true });

	if (!text) return null;

	return (
		<>
			<button
				type="button"
				ref={anchorRef}
				className={`sf-rap-kpi-info ${open ? "is-open" : ""}`}
				aria-label={label}
				aria-expanded={open}
				onClick={(e) => {
					// The KPI card itself is not clickable, but a panel header can be.
					e.stopPropagation();
					clearTimer();
					if (pinned) close();
					else setPinned(true);
				}}
				onMouseEnter={hoverIn}
				onMouseLeave={hoverOut}
				/* Keyboard focus opens it outright — there is no pointer to move
				   away, so the delay would just feel like lag. */
				onFocus={() => {
					clearTimer();
					setHovered(true);
				}}
				onBlur={close}
			>
				<Info size={13} />
			</button>

			{open && (
				<HintBubble
					pos={pos}
					pinned={pinned}
					onClose={close}
					onMouseEnter={clearTimer}
					onMouseLeave={hoverOut}
					label={label}
					bubbleRef={bubbleRef}
				>
					<p>{text}</p>
				</HintBubble>
			)}
		</>
	);
};

export default InfoHint;
